import { useCallback, useMemo } from 'react'
import { Stack, LinearProgress, Button, IconButton, Badge, Tooltip, Chip } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined'
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'
import { systemValueLabel } from '../../../../../generic/systemValues/systemValueLabel'
import JobStatusChip from '../components/JobStatusChip'
import SmartSplitButton from '../../../../../generic/buttons/SmartSplitButton'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'
import useMosInfo from '../../../common/provider/useMosInfo'
import { JOB_ACTION_INTENT, getJobStatusActions, TARGET_STATUS_CODE, resolveJobActionGate } from '../utils/jobStatus'
import { isBlockedByIncidence, isBlockedBySingleJobMode, SINGLE_JOB_MODE_BLOCK_KEY, SINGLE_JOB_MODE_BLOCKED_BUTTON_SX } from '../../../common/canStartJob'

const DEFAULT_HIDDEN_COLUMNS = []
const COLUMN_FIELDS = {
  STATUS: 'status',
  FABRICATION_ORDER: 'fabricationOrder.fabricationOrder',
  // Issue #3048: the name of the FO's part reference, already published by Sun inside the FO of
  // `CompassJobReferenceDto`. Display-only: the Finished tabs neither project it (empty cell) nor
  // whitelist its path, so a sort/filter would silently do nothing there.
  PART_REFERENCE_NAME: 'fabricationOrder.partReference.name',
  JOB: 'node.name',
  ASSET: 'plannedDevice.name',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  // Story 96 (CA4/RN12): the job's MOS queue priority. Technical name `jobPriority`, NEVER `priority`
  // -- that one is the Part's priority (1..10, descending, legacy) and mixing them is risk R4 of the
  // story. Lower number = earlier; absent means "not prioritised" and paints an empty cell (RN1).
  // Sun resolves the default order, the sorting and the column filter; Jupiter only declares it.
  JOB_PRIORITY: 'jobPriority',
  // Story 110 (CA2/RN1): the job's remark -- the free-text note the process engineer (workflow editor)
  // or the planner ("Job Settings" tab, story 96) left on the job. It lives on the job of the process
  // (`job_reference.remark`), so MOS shows EXACTLY the same value those two edit; MOS never edits it
  // (RN2). Read-only text, up to 1000 characters and possibly multiline (RN15). Sun resolves the
  // sorting (absent last, explicitly) and the column filter; Jupiter only declares the column.
  REMARK: 'remark',
  // Story 120 (S99-S105, F1): the job's planning group (`row.planningGroup`), read verbatim from the
  // contract -- never redefined, normalised or trimmed here. Purely informative and silent (POL5/
  // RN10): it lets an operator recognise same-group jobs at a glance, it validates nothing and it
  // never limits a selection. No custom render on purpose, same as JOB_PRIORITY above: the generic
  // scalar path already paints an empty cell for null/empty/whitespace-only/absent (RN64.1/CA2.2),
  // and a bespoke render is exactly what could sneak in a dash or a "no group" filler by accident.
  // Solo visible in this version (G1-5): Sun neither sorts nor filters by it yet, so the column
  // disables both (`sortable: false`, `search: false`) instead of pretending they work.
  PLANNING_GROUP: 'planningGroup',
  // Virtual column: not a real field. Sorting it sends sortKey 'attention' (jobs with open incidences or
  // pending actions first/last); its header search sends a columnFilter 'attention' with the number shown
  // in the icon. Both are resolved server-side.
  ATTENTION: 'attention',
  // Informative column of the "All Active Jobs" tab (S9): whether the job's technology is contained
  // in the querying device's technologies (RN5). Filterable/sortable via the same generic column
  // mechanism as any other column (no bespoke "compatible only" toggle).
  COMPATIBLE: 'compatible',
  // S9 amendment: "All Active Jobs" returns one row per job (OF + jobReference), aggregating every
  // device with an open execution in `row.activeDevices`. The Asset column filters/sorts against
  // this nested path instead of `plannedDevice.name` in that mode (Sun resolves it server-side).
  ACTIVE_DEVICES: 'activeDevices.name',
  ACTIONS: 'actions_field'
}

export const PLANNED_JOB_LIST_CONFIGURABLE_COLUMNS = [
  {
    field: COLUMN_FIELDS.STATUS,
    label: 'Status'
  },
  {
    field: COLUMN_FIELDS.FABRICATION_ORDER,
    label: 'Fabrication Order'
  },
  // Issue #3048: one more entry of the catalog, with no special treatment.
  {
    field: COLUMN_FIELDS.PART_REFERENCE_NAME,
    label: 'Part name'
  },
  {
    field: COLUMN_FIELDS.JOB,
    label: 'Job'
  },
  {
    field: COLUMN_FIELDS.ASSET,
    label: 'Asset'
  },
  {
    field: COLUMN_FIELDS.START_DATE,
    label: 'Start date'
  },
  {
    field: COLUMN_FIELDS.END_DATE,
    label: 'End date'
  },
  // Story 96 (CA18/S87): one more entry of the catalog, with no special treatment -- neither forced
  // visible nor forced hidden. Hiding it does not alter the row order, which lives in the source (D25).
  {
    field: COLUMN_FIELDS.JOB_PRIORITY,
    label: 'Priority'
  },
  // Story 110 (CA2/RN14, G1-9): one more entry of the catalog, with the same "no special treatment"
  // as the priority above -- neither forced visible nor forced hidden. Hiding it is only ceasing to
  // paint it: it changes no row order (that lives in the source) and does not drop the field from
  // the response.
  {
    field: COLUMN_FIELDS.REMARK,
    label: 'Remark'
  },
  // Story 120 (G1-5): one more entry of the catalog, with the same "no special treatment" as
  // Priority (#96) and Remark (#110) above -- neither forced visible nor forced hidden.
  {
    field: COLUMN_FIELDS.PLANNING_GROUP,
    label: 'Group'
  },
  // Story 193 (MIC-4349, RN11): the "Quality" column becomes hideable like the rest -- visible by
  // default (no saved config names it), hidden only when the work centre unchecks it. Hiding it only
  // stops painting it: same jobs, same order, same requests.
  {
    field: COLUMN_FIELDS.ATTENTION,
    label: 'Quality'
  }
]

// Story 110 (CA5/G1-10): the truncation threshold is LAYOUT -- the column's own max width plus the
// CSS ellipsis below -- never a character count in the code, so it adapts to different screens
// instead of cutting at an arbitrary number that looks wrong on all of them.
const REMARK_COLUMN_STYLE = { maxWidth: '18rem' }
const REMARK_CELL_TEXT_STYLE = {
  display: 'block',
  // One single line always (RN15/G1-5): the row of a 1000-character multiline note is exactly as
  // tall as every other row.
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}
// The tooltip is the opposite choice: it respects the note as it was written, line breaks included
// (RN12/G1-5), and carries the WHOLE value with no cap of ours (RN11-bis/G1-4).
const REMARK_TOOLTIP_SLOT_PROPS = { tooltip: { sx: { whiteSpace: 'pre-wrap' } } }

/**
 * Renders the job's remark cell (story 110, CU2): the note on one line, clipped by the column width,
 * with the complete value in a tooltip.
 *
 * RN3/G1-8 -- absent, empty and whitespace-only are THE SAME CASE: an empty cell, with no tooltip to
 * hover. Nothing that could read as a value (no dash, no zero, no placeholder), exactly like the
 * priority column of story 96 (RN1 there).
 *
 * RN11/INV1 -- collapsing the line breaks and clipping the text are PRESENTATION ONLY. The value the
 * tooltip shows, the one Sun filters and the one Sun sorts by are the same single value; this render
 * never shortens the string it was given, it only paints it inside a box that is narrower than it.
 */
// Issue #3048: the Finished tabs send `partReference: null`, so the nested read must not throw.
const renderPartReferenceName = (row) => row.fabricationOrder?.partReference?.name ?? ''

const renderRemark = (row) => {
  const remark = typeof row.remark === 'string' ? row.remark : ''
  if (remark.trim() === '') return ''
  return (
    <Tooltip title={remark} slotProps={REMARK_TOOLTIP_SLOT_PROPS}>
      <span style={REMARK_CELL_TEXT_STYLE}>{remark.replace(/\r\n?|\n/g, ' ')}</span>
    </Tooltip>
  )
}

/**
 * Renders a single quality icon per job: the pending-actions icon (with its count) when the job has
 * actions to execute, otherwise the incidences icon (with its open-incidence count). Both open the same
 * quality incidence widget, where actions are managed. Never shows both icons at once.
 */
const renderQualityIcon = (row, onOpenQualityIncidence, t_) => {
  const disabled = !onOpenQualityIncidence || !row.node || !row.fabricationOrder
  const hasPendingActions = row.pendingActionCount > 0
  const icon = hasPendingActions
    ? { component: <PendingActionsOutlinedIcon />, count: row.pendingActionCount, color: 'error', label: t_('Pending actions') }
    : { component: <GppMaybeOutlinedIcon />, count: row.openIncidenceCount, color: 'warning', label: t_('Open incidences') }

  return (
    <Tooltip title={icon.label}>
      <span>
        <IconButton
          onClick={() => onOpenQualityIncidence(row)}
          disabled={disabled}
        >
          <Badge
            badgeContent={icon.count}
            invisible={!icon.count}
            color={icon.color}
          >
            {icon.component}
          </Badge>
        </IconButton>
      </span>
    </Tooltip>
  )
}

/**
 * Renders the informative compatibility chip for the "All Active Jobs" tab (S9/RN5): whether the
 * job's technology is contained in the querying device's technologies. Purely informative — the
 * column itself is filtered/sorted by the same generic table mechanism as any other column.
 */
const renderCompatibilityChip = (row, t_) => (
  <Chip
    label={row.compatible ? t_('Compatible') : t_('Incompatible')}
    color={row.compatible ? 'success' : 'default'}
    size='small'
  />
)

/**
 * Renders the informative Asset chips for the grouped "All Active Jobs" row (S9 amendment): one
 * chip per device currently running this job (`row.activeDevices`). Degrades gracefully to the
 * plain `plannedDevice.name` text (today's shape) when `activeDevices` is missing or empty, for an
 * older Sun that hasn't shipped the new field yet.
 */
const renderAssetChips = (row) => {
  const devices = Array.isArray(row.activeDevices) ? row.activeDevices : []
  if (devices.length === 0) {
    return row.plannedDevice?.name ?? ''
  }
  return (
    <Stack direction='row' flexWrap='wrap' gap={0.5}>
      {devices.map((device) => (
        <Chip key={device.id} label={device.name} size='small' />
      ))}
    </Stack>
  )
}

/**
 * Renders the status-action cell for a job: a progress bar plus a split button exposing the available
 * status changes (Start / Finish / Interrupt) and a details button. Falls back to a disabled Start
 * button when the job has no available action. Status changes are gated behind a present worker via
 * WorkerRequiredButton.
 *
 * S13/CA6/RN8/S9 amendment: in "grouped active jobs" mode (the "All Active Jobs" tab), the row
 * represents the JOB (aggregating every device running it), not a single device -- there is no
 * longer a per-row device to compare against the querying one. Sun computes
 * `row.joinedByRequestingDevice` directly, installation-wide:
 * - true  -> the querying device is already part of this job: status-derived actions (Finish/
 *   Interrupt), exactly like a row that belonged to it, since `updateStatus` already targets the
 *   querying device (via useMosInfo, not a value read from the row).
 * - false/absent -> single "Join" action (same target status as Start, ACTIVE_EXECUTION).
 *
 * S48 (CA11/RN24/D18, story 86): a job whose lifecycle is governed by the acceptance form
 * (`row.governedByAcceptance`) never gets a status-change action offered here -- Sun's guard would
 * reject any of them anyway (E1 `JobGovernedByAcceptance`); gating here just spares the round trip
 * (P3: the server guard remains the real safety net, this is UX prevention only). It reuses the
 * SAME disabled-Start fallback the cell already renders for a job with zero available actions, and
 * adds a tooltip so the operator knows why (PO addendum, on top of the signed spec). Gated
 * deliberately in THIS render function -- not in `getJobStatusActions` (utils/jobStatus.js), whose
 * signature (status -> actions) is a separate contract that the "Join" offer below never goes
 * through, and not in `JobStatusChangeDialog`/`useJobStatusChange`, which 6 call sites share and
 * would only see the rejection after the operator already clicked.
 *
 * S50: the gate is checked BEFORE `offersJoin` on purpose -- the grouped "All Active Jobs" tab must
 * not offer "Join" on the MOD job either, for the exact same reason (Sun's guard rejects it too).
 *
 * Story 100 (S75/S76/S78, RN34): a JOB that crossed the external circuit gate (RN1) is gated here
 * too, on top of (not instead of) the acceptance-form gate above -- same "never hidden, never
 * filtered, never an error, disabled with a tooltip naming the reason" shape, checked BEFORE
 * `offersJoin` for the exact same reason as S50 (the Join offer must never reach an ungated job).
 * The two gates are mutually exclusive in practice (a MOD job and an external-of-the-circuit job are
 * different domain concepts), so only one tooltip ever applies.
 *
 * Single Job Mode (`isStartBlockedBySingleJobMode(row)`, from `common/canStartJob`): while the
 * Operation widget's setting is on and the machine already runs ANOTHER job, every START-intent action
 * of the row (Start, the Resume of an INTERRUPTED job, and Join on the grouped tab) KEEPS its label and
 * its place -- a row must never suddenly read "Finish" -- but LOOKS disabled, carries the tooltip, and
 * on click runs `onStartBlocked(row)` (the surface explains why) instead of starting anything.
 * Finish/Interrupt stay exactly as they are: they are what the operator needs to do next.
 */
const renderStatusActions = (row, onStatusChange, onShowDetails, blockedByIncidenceStatus, t_, groupedActiveJobsMode, isStartBlockedBySingleJobMode = () => false, onStartBlocked = () => {}) => {
  const progress = row.total > 0 ? (row.done / row.total) * 100 : 0
  // Story 160 (R-16): the gate and its tooltip come from the single rule the job detail shares.
  const { gated, tooltipKey } = resolveJobActionGate(row)
  const gatedTooltip = gated ? t_(tooltipKey) : ''
  const offersJoin = groupedActiveJobsMode && row.joinedByRequestingDevice !== true
  // Story 113 (S114, R-F): the intent rides along with the target code. It comes from the action
  // itself (`getJobStatusActions`), so the Start offered on an INTERRUPTED job -- a RESUME -- carries
  // the START intent with no special case, which is exactly what S166-S168 need.
  const mapStatusAction = (statusAction) => ({
    label: t_(statusAction.label),
    intent: statusAction.intent,
    action: () => onStatusChange(row, statusAction.statusCode, statusAction.intent)
  })
  const allStatusActions = gated
    ? []
    : offersJoin
      // S113: joining a job already active on another machine IS a start, so it carries that intent.
      ? [{ label: t_('Join'), intent: JOB_ACTION_INTENT.START, action: () => onStatusChange(row, TARGET_STATUS_CODE.START, JOB_ACTION_INTENT.START) }]
      : getJobStatusActions(row.status).map(mapStatusAction)
  const singleJobBlocked = !gated && isStartBlockedBySingleJobMode(row)
  const withholdsStart = singleJobBlocked && allStatusActions.some((statusAction) => statusAction.intent === JOB_ACTION_INTENT.START)
  const withholdStart = (statusAction) => (statusAction.intent === JOB_ACTION_INTENT.START
    ? { ...statusAction, sx: SINGLE_JOB_MODE_BLOCKED_BUTTON_SX, action: () => onStartBlocked(row) }
    : statusAction)
  const statusActions = withholdsStart ? allStatusActions.map(withholdStart) : allStatusActions
  // The tooltip on the actions button: the acceptance/external gate wins, then Single Job Mode.
  const disabledStartTooltip = gated
    ? gatedTooltip
    : (withholdsStart ? t_(SINGLE_JOB_MODE_BLOCK_KEY) : '')
  // Keep a disabled Start button when the job has no available action.
  const hasActions = statusActions.length > 0
  const buttonActions = hasActions
    ? statusActions
    : [{ label: t_('Start'), disabled: true }]
  const statusActionsButton = (
    <SmartSplitButton
      actions={buttonActions}
      disabled={!hasActions || blockedByIncidenceStatus}
      ButtonComponent={WorkerRequiredButton}
    />
  )
  return (
    <Stack direction='row-reverse' sx={{ width: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'max-content max-content', gap: '0.5rem', alignItems: 'center' }}>
        <LinearProgress
          sx={{ width: '10rem', gridColumn: '1 / 3' }}
          variant='determinate'
          value={progress}
        />
        {disabledStartTooltip !== ''
          ? (
            <Tooltip title={disabledStartTooltip}>
              <span>{statusActionsButton}</span>
            </Tooltip>
            )
          : statusActionsButton}
        <Button
          variant='outlined'
          onClick={() => onShowDetails(row)}
        >
          <SearchIcon />
        </Button>
      </div>
    </Stack>
  )
}

/**
 * Historia 120 (enmienda 05, RN8.1/S111/S211) -- the checkbox `generic/SmartMateriaUITable.jsx`
 * renders for its own `multipleSelection` mechanism exposes NO way to attach a tooltip to it (no
 * `title` prop, no per-row render slot for that cell), and the file is not touched by this story
 * (S114). §7.2 #13/S113 also forbids adding any new column for this. The literal `t_(row.status)`
 * this scenario asks for is therefore attached here instead, to the EXISTING "Status" column's chip
 * -- same data, same translation key, zero new columns, zero changes to the generic table.
 *
 * @param {Object} row
 * @returns {JSX.Element}
 */
const renderStatusCell = (row, t_) => (
  <Tooltip title={systemValueLabel(t_, 'DeviceFabricationOrderStatusTypeEnum', row.status || '')}>
    <span><JobStatusChip status={row.status} statusType={row.statusType} /></span>
  </Tooltip>
)

/**
 * Hook to define table columns for job lists.
 *
 * @param {Object} params
 * @param {Function} params.onStatusChange - Callback to open status change dialog
 * @param {Function} params.onShowDetails - Callback to show job details
 * @param {Array} params.hiddenColumns - Hidden table column fields
 * @param {Function} params.onOpenQualityIncidence - Callback to open Quality Incidence widget for the row
 * @param {boolean} [params.showCompatibilityColumn=false] - Adds the informative "compatible" column
 * used by the "All Active Jobs" tab (S9). Omitted for the other tabs (no regression, D2).
 * @param {boolean} [params.groupedActiveJobsMode=false] - Used only by the "All Active Jobs" tab
 * (S9 amendment/S13/CA6/RN8): the row represents a JOB aggregating every device running it. The
 * Asset column renders one chip per `row.activeDevices` (falling back to plain `plannedDevice.name`
 * text if absent/empty) and filters on `activeDevices.name`; the actions cell offers "Join" unless
 * `row.joinedByRequestingDevice` is true, in which case it falls back to the usual status-derived
 * Finish/Interrupt. Omitted for the other tabs (no regression, D2).
 * @param {boolean} [params.batchSelectionEnabled=false] - Historia 120 (enmienda 05, S111/S211): only
 * the widget's OWN main list turns this on (never "Show all"'s tabs, S227). Wraps the Status chip in
 * a `Tooltip` carrying the row's own published literal -- see `renderStatusCell` above.
 * @returns {Array} Table columns configuration
 */
const useJobColumns = ({ onStatusChange, onShowDetails, hiddenColumns = DEFAULT_HIDDEN_COLUMNS, onOpenQualityIncidence, showCompatibilityColumn = false, groupedActiveJobsMode = false, batchSelectionEnabled = false, onStartBlocked = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { globalData, activeJobs } = useMosInfo()
  // Issue #3724: driven by the "Blocks production" flag of the incidence pick list (Sun's
  // incidence.blocksProduction), no longer hardcoded to the STOPPED/IDLE device status.
  const blockedByIncidenceStatus = isBlockedByIncidence(globalData?.incidence)
  // Single Job Mode: per row, because the job that is itself the active one must not be blocked by
  // its own presence. `activeJobs` is the polled active context of the machine.
  const isStartBlockedBySingleJobMode = useCallback((row) => isBlockedBySingleJobMode({
    globalData,
    activeJobs,
    fabricationOrderId: row?.fabricationOrder?.id,
    jobReferenceId: row?.jobReferenceId
  }), [globalData, activeJobs])
  const hiddenColumnSet = useMemo(() => new Set(Array.isArray(hiddenColumns) ? hiddenColumns : DEFAULT_HIDDEN_COLUMNS), [hiddenColumns])

  return useMemo(() => [
    {
      name: t_('Status'),
      field: COLUMN_FIELDS.STATUS,
      readOnly: true,
      sortable: false,
      hide: hiddenColumnSet.has(COLUMN_FIELDS.STATUS),
      render: (row) => batchSelectionEnabled
        ? renderStatusCell(row, t_)
        : <JobStatusChip status={row.status} statusType={row.statusType} />
    },
    {
      name: t_('Fabrication Order'),
      field: COLUMN_FIELDS.FABRICATION_ORDER,
      readOnly: true,
      hide: hiddenColumnSet.has(COLUMN_FIELDS.FABRICATION_ORDER)
    },
    // Issue #3048: same `groupedActiveJobsMode` exclusion as Priority/Remark/Group, so the
    // "All Active Jobs" column set stays untouched. `search: false`, never `null` (see Group below).
    ...(groupedActiveJobsMode
      ? []
      : [{
          name: t_('Part name'),
          field: COLUMN_FIELDS.PART_REFERENCE_NAME,
          readOnly: true,
          sortable: false,
          search: false,
          disableGenericSearch: true,
          hide: hiddenColumnSet.has(COLUMN_FIELDS.PART_REFERENCE_NAME),
          render: renderPartReferenceName
        }]),
    {
      name: t_('Job'),
      field: COLUMN_FIELDS.JOB,
      readOnly: true,
      hide: hiddenColumnSet.has(COLUMN_FIELDS.JOB)
    },
    {
      name: t_('Asset'),
      // S9 amendment: "All Active Jobs" filters/sorts on `activeDevices.name` (installation-wide,
      // resolved server-side); the other 3 tabs keep the plain per-device `plannedDevice.name`.
      field: groupedActiveJobsMode ? COLUMN_FIELDS.ACTIVE_DEVICES : COLUMN_FIELDS.ASSET,
      readOnly: true,
      hide: hiddenColumnSet.has(COLUMN_FIELDS.ASSET),
      ...(groupedActiveJobsMode ? { render: renderAssetChips } : {})
    },
    {
      name: t_('Start date'),
      field: COLUMN_FIELDS.START_DATE,
      type: 'datetime',
      readOnly: true,
      hide: hiddenColumnSet.has(COLUMN_FIELDS.START_DATE)
    },
    {
      name: t_('End date'),
      field: COLUMN_FIELDS.END_DATE,
      type: 'datetime',
      readOnly: true,
      hide: hiddenColumnSet.has(COLUMN_FIELDS.END_DATE)
    },
    // Story 96 (CA4/RN12/P8/D32): the queue priority as a plain read-only scalar column of the SHARED
    // set, so the five sources that share `CompassJobReferenceDto` get it at once -- the device
    // queue's main list, the All Planned / Compatible / Reworks / Finished tabs of the modal and the
    // assembly job selector -- with no conditional per source or per widget (one rule for the five).
    // No custom render on purpose: the generic scalar path paints nothing when the value is absent,
    // which is exactly RN1/S13 (no zero, no dash, nothing that reads as a value).
    // `groupedActiveJobsMode` is the ONLY exclusion (S84/RN11): the "All Active Jobs" tab is fed by a
    // different DTO and pipeline that carries no `jobPriority`. It reuses the flag that tab already
    // passes for its other differences rather than adding a conditional of this story's own.
    // Story 110 (CA2/RN4/RN6, G1-7) rides on that very same exclusion instead of adding one of its
    // own: the remark column joins the SHARED set right behind the priority, so the same five sources
    // get it at once (device queue, All Planned / Compatible / Reworks / Finished, plus the assembly
    // selector that mounts the same modal) and "All Active Jobs" keeps its column set untouched
    // because it already passes `groupedActiveJobsMode` for its other differences. The "External
    // jobs" tab needs nothing here at all: it has its own hook (`useExternalCircuitTabColumns`).
    // Unlike the priority, this one DOES carry a render (CU2: one line, clipped by the column width,
    // full value in a tooltip); the empty-cell-for-absent guarantee moves into `renderRemark`.
    ...(groupedActiveJobsMode
      ? []
      : [{
          name: t_('Priority'),
          field: COLUMN_FIELDS.JOB_PRIORITY,
          readOnly: true,
          hide: hiddenColumnSet.has(COLUMN_FIELDS.JOB_PRIORITY)
        },
        {
          name: t_('Remark'),
          field: COLUMN_FIELDS.REMARK,
          readOnly: true,
          hide: hiddenColumnSet.has(COLUMN_FIELDS.REMARK),
          style: REMARK_COLUMN_STYLE,
          render: renderRemark
        },
        // Story 120 (S99-S105): rides on the SAME exclusion as Priority and Remark above -- the
        // "All Active Jobs" tab already passes `groupedActiveJobsMode` for its other differences, and
        // its DTO (`CompassActiveJobDto`) never carries `planningGroup`, so the column would only be
        // an always-empty one there (G1-5/S103). No render of our own (S100/RN64.1/CA2.2): the
        // generic scalar path paints an empty cell for null/empty/whitespace-only/absent, with no
        // dash and no filler text. `sortable: false` + `search: false` keep it solely visual in this
        // version (S101/G1-5): no sort/filter criterion for the group ever reaches the server.
        // `search: false`, never `null`: the generic table paints an EMPTY cell in the search row for
        // `false`, but NO cell at all for `null` -- which shifts every search box one column left and
        // leaves a transparent hole over the actions column that the scrolled rows show through.
        {
          name: t_('Group'),
          field: COLUMN_FIELDS.PLANNING_GROUP,
          readOnly: true,
          sortable: false,
          search: false,
          hide: hiddenColumnSet.has(COLUMN_FIELDS.PLANNING_GROUP)
        }]),
    {
      // Sortable + searchable so the header offers "change direction" (sort by attention) and a search box
      // above the icon (search by the number shown). The icon itself is rendered per row.
      name: t_('Quality'),
      field: COLUMN_FIELDS.ATTENTION,
      sortable: true,
      render: (row) => renderQualityIcon(row, onOpenQualityIncidence, t_),
      hide: hiddenColumnSet.has(COLUMN_FIELDS.ATTENTION)
    },
    ...(showCompatibilityColumn
      ? [{
          name: t_('Compatibility'),
          field: COLUMN_FIELDS.COMPATIBLE,
          sortable: true,
          render: (row) => renderCompatibilityChip(row, t_)
        }]
      : []),
    {
      name: '',
      field: COLUMN_FIELDS.ACTIONS,
      sortable: false,
      render: (row) => renderStatusActions(row, onStatusChange, onShowDetails, blockedByIncidenceStatus, t_, groupedActiveJobsMode, isStartBlockedBySingleJobMode, onStartBlocked)
    }
  ], [t_, hiddenColumnSet, onStatusChange, onShowDetails, onOpenQualityIncidence, blockedByIncidenceStatus, showCompatibilityColumn, groupedActiveJobsMode, batchSelectionEnabled, isStartBlockedBySingleJobMode, onStartBlocked])
}

export default useJobColumns
